import React, {
  createContext,
  useState,
  useContext,
  useLayoutEffect,
  useCallback,
} from "react";
import authService from "../services/authService";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem("authData");
    if (!stored) return null;
    try {
      return JSON.parse(stored).user;
    } catch {
      return null;
    }
  });

  const [loading, setLoading] = useState(true);

  useLayoutEffect(() => {
    const savedAuth = localStorage.getItem("authData");

    const hydrateAuth = async () => {
      try {
        if (!savedAuth) return;

        const parsed = JSON.parse(savedAuth);
        setUser(parsed.user);

        const token = parsed?.token;
        if (!token) return;

        const response = await authService.getMe(token);
        const updatedAuth = { user: response.user, token };
        localStorage.setItem("authData", JSON.stringify(updatedAuth));
        setUser(response.user);
      } catch {
        localStorage.removeItem("authData");
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    hydrateAuth();
  }, []);

  const login = useCallback((userData, token) => {
    const authData = { user: userData, token };
    localStorage.setItem("authData", JSON.stringify(authData));
    setUser(userData);
  }, []);

  const refreshUser = useCallback(async () => {
    const authData = localStorage.getItem("authData");
    if (!authData) return null;
    const parsed = JSON.parse(authData);
    const token = parsed?.token;
    if (!token) return null;

    const response = await authService.getMe(token);
    const updatedAuth = { user: response.user, token };
    localStorage.setItem("authData", JSON.stringify(updatedAuth));
    setUser(response.user);
    return response.user;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("authData");
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, login, logout, refreshUser, isAuthenticated: !!user, loading }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
